import React, { useState, useEffect } from 'react';
import { BookOpen, XCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import HomeworkFilters from './homework/HomeworkFilters';
import HomeworkSubmissionInterface from './homework/HomeworkSubmissionInterface';
import HomeworkReviewInterface from './homework/HomeworkReviewInterface';
import HomeworkList from './homework/HomeworkList';

const StudentHomeworkPortal = () => {
  const { isAuthenticated, isStudent, loading: authLoading } = useAuth();
  const [homeworks, setHomeworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  useEffect(() => {
    if (isAuthenticated && isStudent && !authLoading) {
      fetchHomework();
    } else if (!authLoading) {
      setLoading(false);
      if (!isAuthenticated) {
        setError('Please log in to view your homework');
      } else if (!isStudent) {
        setError('Access denied. Student account required.');
      }
    }
  }, [isAuthenticated, isStudent, authLoading]);

  const fetchHomework = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/homework/student/they`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        setError('Please log in to view your homework');
        return;
      }

      if (response.status === 403) {
        setError('Access denied. Student account required.');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch homework');
      }

      const data = await response.json();
      setHomeworks(data || []);
      setError(null);
    } catch (err) {
      setError('Unable to load homework. Please try again later.');
      console.error('Error fetching homework:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitClick = (homework) => {
    setSelectedHomework(homework);
    setShowSubmissionModal(true);
  };

  const handleReviewClick = (homework) => {
    setSelectedHomework(homework);
    setShowReviewModal(true);
  };

  const handleSubmissionComplete = (homeworkId) => {
    fetchHomework();
    setShowSubmissionModal(false);
    setSelectedHomework(null);
  };

  const statusCounts = homeworks.reduce((acc, hw) => {
    const status = hw.status || 'Assigned';
    const displayStatus = status === 'GRADED' ? 'Completed' : status;
    acc[displayStatus] = (acc[displayStatus] || 0) + 1;
    return acc;
  }, {});

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your homework...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !isStudent) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md mx-auto text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600 mb-4">
            {!isAuthenticated ? 'Please log in to view your homework.' : 'This portal is only available to students.'}
          </p>
          <button
            onClick={() => window.location.href = '/login'}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            {!isAuthenticated ? 'Go to Login' : 'Go to Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md mx-auto">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Homework</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchHomework}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6 border">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-blue-600" />
              My Homework
            </h1>
            <p className="text-gray-600 mt-1">
              Manage and submit your homework assignments
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-600">{homeworks.length}</div>
            <div className="text-sm text-gray-600">Total Assignments</div>
          </div>
        </div>
      </div>

      <HomeworkFilters
        filter={filter}
        setFilter={setFilter}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusCounts={statusCounts}
        totalCount={homeworks.length}
      />

      <HomeworkList
        homeworks={homeworks}
        filter={filter}
        searchTerm={searchTerm}
        onSubmitClick={handleSubmitClick}
        onReviewClick={handleReviewClick}
      />

      {showSubmissionModal && selectedHomework && (
        <HomeworkSubmissionInterface
          homework={selectedHomework}
          onSubmissionComplete={handleSubmissionComplete}
          onClose={() => {
            setShowSubmissionModal(false);
            setSelectedHomework(null);
          }}
        />
      )}

      {showReviewModal && selectedHomework && (
        <HomeworkReviewInterface
          homework={selectedHomework}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedHomework(null);
          }}
        />
      )}
    </div>
  );
};

export default StudentHomeworkPortal;